import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AlertTriangle, CheckCircle2, Eye, Edit3, Loader2, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_JUST_BADGE = {
  pendente:               { label: "PENDENTE",           cls: "bg-red-100 text-red-800 border-red-300" },
  aguardando_aprovacao:   { label: "AGUARD. APROV.",     cls: "bg-amber-100 text-amber-800 border-amber-300" },
  aprovada:               { label: "APROVADA",           cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const MOTIVOS = [
  "Falta de equipamento no estoque",
  "Problema logístico (transporte)",
  "Aguardando aprovação interna",
  "Problema com o cliente",
  "Força maior",
  "Outro",
];

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

function ModalPreencher({ just, open, onOpenChange, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [acao, setAcao] = useState("");
  const [previsao, setPrevisao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) {
      setMotivo(""); setDescricao(""); setAcao(""); setPrevisao(""); setSalvando(false);
    }
  }, [open]);

  const submit = async () => {
    if (!motivo || !descricao.trim()) {
      toast.error("Motivo e descrição são obrigatórios"); return;
    }
    setSalvando(true);
    try {
      await api.post(`/tickets-ops/justificativas/${just.id}/preencher`, {
        motivo, descricao, acao_corretiva: acao, previsao_resolucao: previsao || null,
      });
      toast.success("Justificativa enviada para aprovação");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally { setSalvando(false); }
  };

  if (!just) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="modal-preencher-just">
        <DialogHeader>
          <DialogTitle>Justificar atraso · Ticket #{just.ticket_id?.slice(0, 8)}</DialogTitle>
          <DialogDescription>
            Atraso de {just.horas_atraso?.toFixed(0)}h · Cliente: {just.cliente_nome}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Motivo do atraso *</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="just-motivo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Descrição detalhada *</Label>
            <Textarea
              value={descricao} onChange={(e) => setDescricao(e.target.value)}
              rows={3} data-testid="just-descricao"
              placeholder="Detalhe o que aconteceu..."
            />
          </div>
          <div>
            <Label className="text-xs uppercase">Ação corretiva tomada</Label>
            <Textarea
              value={acao} onChange={(e) => setAcao(e.target.value)}
              rows={2} data-testid="just-acao"
              placeholder="Como evitar que aconteça novamente..."
            />
          </div>
          <div>
            <Label className="text-xs uppercase">Previsão de resolução</Label>
            <Input type="date" value={previsao} onChange={(e) => setPrevisao(e.target.value)} data-testid="just-previsao" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={submit} disabled={salvando} data-testid="just-submit" className="bg-orange-600 hover:bg-orange-700">
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Enviar justificativa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModalAprovar({ just, open, onOpenChange, onDone }) {
  const [obs, setObs] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { if (open) { setObs(""); setSalvando(false); } }, [open]);

  const aprovar = async () => {
    setSalvando(true);
    try {
      await api.patch(`/tickets-ops/justificativas/${just.id}/aprovar`, { observacao: obs });
      toast.success("Justificativa aprovada");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aprovar");
    } finally { setSalvando(false); }
  };

  if (!just) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="modal-aprovar-just">
        <DialogHeader>
          <DialogTitle>Aprovar justificativa</DialogTitle>
          <DialogDescription>Cliente: {just.cliente_nome} · Atraso: {just.horas_atraso?.toFixed(0)}h</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded p-2 text-xs space-y-1">
            <div><strong>Motivo:</strong> {just.motivo}</div>
            <div><strong>Descrição:</strong> {just.descricao}</div>
            {just.acao_corretiva && <div><strong>Ação corretiva:</strong> {just.acao_corretiva}</div>}
            <div className="text-slate-500">Por {just.preenchido_por_nome} em {fmtData(just.preenchido_em)}</div>
          </div>
          <div>
            <Label className="text-xs uppercase">Observação do aprovador (opcional)</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="aprovar-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={aprovar} disabled={salvando} data-testid="aprovar-confirm" className="bg-emerald-600 hover:bg-emerald-700">
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Aprovar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModalVer({ just, open, onOpenChange }) {
  if (!just) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="modal-ver-just">
        <DialogHeader>
          <DialogTitle>Detalhes da justificativa</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          <div><strong>Ticket:</strong> #{just.ticket_id?.slice(0, 8)} · {just.tipo}</div>
          <div><strong>Cliente:</strong> {just.cliente_nome}</div>
          <div><strong>Atraso:</strong> {just.horas_atraso?.toFixed(0)}h</div>
          <div><strong>Status:</strong> <Badge className={STATUS_JUST_BADGE[just.status_just]?.cls + " border text-[10px]"}>{STATUS_JUST_BADGE[just.status_just]?.label}</Badge></div>
          {just.motivo && (
            <div className="bg-slate-50 rounded p-2 space-y-1">
              <div><strong>Motivo:</strong> {just.motivo}</div>
              <div><strong>Descrição:</strong> {just.descricao}</div>
              {just.acao_corretiva && <div><strong>Ação corretiva:</strong> {just.acao_corretiva}</div>}
              {just.previsao_resolucao && <div><strong>Previsão:</strong> {just.previsao_resolucao}</div>}
              <div className="text-slate-500 pt-1">Preenchido por {just.preenchido_por_nome} em {fmtData(just.preenchido_em)}</div>
            </div>
          )}
          {just.status_just === "aprovada" && (
            <div className="bg-emerald-50 rounded p-2">
              <div className="text-emerald-700"><ShieldCheck size={12} className="inline mr-1" /> Aprovada por {just.aprovado_por_nome} em {fmtData(just.aprovado_em)}</div>
              {just.observacao_aprovador && <div className="text-slate-700 mt-1">Obs: {just.observacao_aprovador}</div>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function JustificativasTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [modal, setModal] = useState({ tipo: null, just: null });

  const podePreencher = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);
  const podeAprovar = ["diretor", "gerente", "supervisao"].includes(user?.role);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets-ops/justificativas", {
        params: filtroStatus ? { status_just: filtroStatus } : {},
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar justificativas");
    } finally { setLoading(false); }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!loading && items.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="justificativas-empty">
        <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
        <p className="text-lg font-bold text-slate-700">NÃO HÁ JUSTIFICATIVA PENDENTE NO MOMENTO!</p>
        <p className="text-sm text-slate-500 mt-2">Quando um ticket for concluído fora do prazo de 72h, aparecerá aqui.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="justificativas-tab">
      <div className="flex items-center gap-2">
        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
          className="border rounded px-3 py-1.5 text-xs"
          data-testid="filtro-just-status"
        >
          <option value="">Todos os status</option>
          <option value="pendente">Pendente</option>
          <option value="aguardando_aprovacao">Aguardando aprovação</option>
          <option value="aprovada">Aprovada</option>
        </select>
        <Button size="sm" variant="outline" onClick={carregar}>Atualizar</Button>
      </div>

      <Card data-testid="tabela-justificativas">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">TICKET</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead className="w-32">CONCLUSÃO</TableHead>
              <TableHead className="w-20">ATRASO</TableHead>
              <TableHead className="w-36">STATUS</TableHead>
              <TableHead className="w-32">PRAZO 72h</TableHead>
              <TableHead className="text-right">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8">
                <Loader2 className="animate-spin inline" />
              </TableCell></TableRow>
            )}
            {!loading && items.map((j) => (
              <TableRow key={j.id} data-testid={`row-just-${j.id}`}>
                <TableCell className="font-mono text-[11px]">#{j.ticket_id?.slice(0, 6)}</TableCell>
                <TableCell className="text-xs">{j.cliente_nome}</TableCell>
                <TableCell className="text-[11px]">{fmtData(j.concluido_em)}</TableCell>
                <TableCell className="text-[11px] font-bold text-red-700">{j.horas_atraso?.toFixed(0)}h</TableCell>
                <TableCell>
                  <Badge className={STATUS_JUST_BADGE[j.status_just]?.cls + " border text-[10px] font-mono"}>
                    {STATUS_JUST_BADGE[j.status_just]?.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-[11px]">
                  {j.status_just === "pendente" ? (
                    j.prazo_preenchimento_vencido ? (
                      <span className="text-red-700 font-bold animate-pulse">
                        <AlertTriangle size={10} className="inline mr-1" /> ESCALADO
                      </span>
                    ) : (
                      <span className="text-amber-700">
                        <Clock size={10} className="inline mr-1" />
                        {j.prazo_preenchimento_horas?.toFixed(0)}h
                      </span>
                    )
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setModal({ tipo: "ver", just: j })} data-testid={`ver-just-${j.id}`}>
                      <Eye size={12} />
                    </Button>
                    {j.status_just === "pendente" && podePreencher && (
                      <Button
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700 h-7 text-[10px]"
                        onClick={() => setModal({ tipo: "preencher", just: j })}
                        data-testid={`preencher-${j.id}`}
                      >
                        <Edit3 size={12} className="mr-1" /> Justificar
                      </Button>
                    )}
                    {j.status_just === "aguardando_aprovacao" && podeAprovar && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[10px]"
                        onClick={() => setModal({ tipo: "aprovar", just: j })}
                        data-testid={`aprovar-${j.id}`}
                      >
                        <ShieldCheck size={12} className="mr-1" /> Aprovar
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ModalPreencher
        just={modal.tipo === "preencher" ? modal.just : null}
        open={modal.tipo === "preencher"}
        onOpenChange={(v) => !v && setModal({ tipo: null, just: null })}
        onDone={carregar}
      />
      <ModalAprovar
        just={modal.tipo === "aprovar" ? modal.just : null}
        open={modal.tipo === "aprovar"}
        onOpenChange={(v) => !v && setModal({ tipo: null, just: null })}
        onDone={carregar}
      />
      <ModalVer
        just={modal.tipo === "ver" ? modal.just : null}
        open={modal.tipo === "ver"}
        onOpenChange={(v) => !v && setModal({ tipo: null, just: null })}
      />
    </div>
  );
}
